import type { QSelectOption } from 'quasar'
import { useI18n } from 'vue-i18n'

import type { LabelWithLocale, SlugWithLabel } from '@/types/api'

export const useTransformField = () => {
  const { t, locale } = useI18n()

  const getLocalizedLabel = (label?: string | LabelWithLocale[]) =>
    Array.isArray(label)
      ? label?.find((l) => l.locale === locale.value)?.value || ''
      : label || ''

  const makeQuasarOption = (slugWithLabel?: SlugWithLabel) => ({
    label: getLocalizedLabel(slugWithLabel?.label),
    value: slugWithLabel?.slug || '',
  })

  const makeQuasarOptions = (slugWithLabels?: SlugWithLabel[]) =>
    slugWithLabels?.map((slugWithLabel) => makeQuasarOption(slugWithLabel))

  const getLabelFromSlug = (slug: string, slugWithLabels?: SlugWithLabel[]) =>
    getLocalizedLabel(
      slugWithLabels?.find((slugWithLabel) => slugWithLabel.slug === slug)
        ?.label,
    )

  const getLabelFromOptions = (
    slug: string | undefined,
    quasarOptions: ReturnType<typeof makeQuasarOptions>,
  ) =>
    quasarOptions?.find((slugWithLabel) => slugWithLabel?.value === slug)?.label
    || ''

  const getValuesFromQuasarOptions = (options?: QSelectOption[]) =>
    options?.map((o) => o.value)

  const minifyNumber = (value: number) => {
    if (!value) return value.toString()

    let suffix = ''

    let divider = 1

    if (value < 1000) {
      return value.toString()
    } else if (value < 1000000) {
      suffix = ` ${t('priceTransform.thousand')} `
      divider = 1000
    } else if (value < 1000000000) {
      suffix = ` ${t('priceTransform.million')} `
      divider = 1000000
    } else {
      suffix = ` ${t('priceTransform.billion')} `
      divider = 1000000000
    }

    const minifiedNumber = (value / divider).toLocaleString('el-GR', {
      maximumFractionDigits: 1,
      minimumFractionDigits: 0,
    })

    return `${minifiedNumber}${suffix}`
  }

  return {
    getLabelFromOptions,
    getLabelFromSlug,
    getLocalizedLabel,
    getValuesFromQuasarOptions,
    makeQuasarOption,
    makeQuasarOptions,
    minifyNumber,
  }
}
